import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Paper,
  TextField,
  MenuItem,
  Button,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { SdkInstall } from '../components/SdkInstall.js';
import { OnboardingCredentials } from '../components/OnboardingCredentials.js';
import { useT } from '../i18n/useT.js';

interface Plan {
  code: string;
  name: string;
  active: boolean;
}

interface OnboardResult {
  organizationId: string;
  organizationName: string;
  ownerUserId: string;
  email: string;
  planCode: string;
  projectId: string;
  publicKey: string;
  apiKey: string;
  emailSent: boolean;
  setupLink?: string;
}

/**
 * Onboards a customer from two fields. One submit creates the company, its
 * owner, a project and the ingest key, and mails the owner a link to set their
 * password — so the admin can hand over a working snippet in the same breath
 * rather than assembling a tenant screen by screen.
 */
export function AdminOnboardingPage() {
  const all = useT();
  const t = all.admin.onboarding;
  const needSuperAdmin = all.admin.needSuperAdmin;
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [planCode, setPlanCode] = useState('free');
  const [result, setResult] = useState<OnboardResult | null>(null);

  const plans = useQuery({
    enabled: !!me?.isSuperAdmin,
    queryKey: ['admin-plans'],
    queryFn: async () => (await api.get('/v1/admin/plans')).data.data as Plan[],
  });

  const onboard = useMutation({
    mutationFn: async () =>
      (
        await api.post('/v1/admin/onboarding', {
          companyName: companyName.trim(),
          email: email.trim(),
          ...(fullName.trim() ? { fullName: fullName.trim() } : {}),
          planCode,
        })
      ).data as OnboardResult,
    onSuccess: (data) => {
      setResult(data);
      setCompanyName('');
      setEmail('');
      setFullName('');
      // The new tenant should show up on Companies without a reload.
      qc.invalidateQueries({ queryKey: ['admin-orgs'] });
    },
  });

  if (me && !me.isSuperAdmin) {
    return <Alert severity="error">{needSuperAdmin}</Alert>;
  }

  const error = onboard.error as { response?: { data?: { error?: { message?: string } } } } | null;
  const canSubmit = companyName.trim().length > 0 && /.+@.+\..+/.test(email.trim());

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <RocketLaunchIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.subtitle}
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t.company}
              placeholder={t.companyPlaceholder}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <TextField
              label={t.ownerEmail}
              placeholder="ibrahim@pepsi.com.tr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              required
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t.fullName}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              select
              label={t.plan}
              value={planCode}
              onChange={(e) => setPlanCode(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="free">{t.planFree}</MenuItem>
              {(plans.data ?? [])
                .filter((p) => p.active && p.code !== 'free')
                .map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    {p.name}
                  </MenuItem>
                ))}
            </TextField>
          </Stack>
          <Box>
            <Button
              variant="contained"
              disabled={!canSubmit || onboard.isPending}
              onClick={() => onboard.mutate()}
              startIcon={onboard.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {onboard.isPending ? t.creating : t.create}
            </Button>
          </Box>
          {error && (
            <Alert severity="error">
              {error.response?.data?.error?.message ?? t.failed}
            </Alert>
          )}
        </Stack>
      </Paper>

      {result && (
        <Box sx={{ mt: 3, maxWidth: 900 }}>
          <Alert severity={result.emailSent ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {result.emailSent ? (
              <>
                <strong>{result.organizationName}</strong> {t.readyEmailed(result.email)}
              </>
            ) : (
              <>
                <strong>{result.organizationName}</strong> {t.readyNoEmail(result.email)}
              </>
            )}
          </Alert>

          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {t.credentialsFor(result.organizationName)}
            </Typography>
            <OnboardingCredentials
              data={{
                projectId: result.projectId,
                apiKey: result.apiKey,
                publicKey: result.publicKey,
                setupLink: result.setupLink,
              }}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              The ingest key is public by design. It is embedded in the customer's client-side code,
              like an analytics write key. The owner sees this same snippet under API Keys once they
              activate their account.
            </Typography>
          </Paper>

          <Box sx={{ mt: 3 }}>
            <SdkInstall projectId={result.projectId} apiKey={result.apiKey} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
